import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {combineLatest, distinctUntilChanged, filter, map, Subscription} from "rxjs";
import {FormBuilder, Validators} from "@angular/forms";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {MatDialog} from "@angular/material/dialog";
import {
    isDealPageLoading,
    selectIsAnyDealFormInEditMode,
    selectSelectedDealClosingDate,
    selectSelectedDealErrorMsg,
    selectSelectedDealId,
    selectSelectedDealIsArchived,
    selectSelectedDealName
} from "../store/deal/deal.selectors";
import {DealActions, DealNameActions} from "../store/deal.actions";
import {CommentCountsActions} from "../../comment/store/comment.actions";
import {CARD_COMMENT_CONTEXT_PREFIX} from "../../shared/comments/card-context-menu/card-comment-context";
import {selectSelectedSourceDataset} from "../../import/store/import.selectors";
import {selectSelectedSeller} from "../store/seller/seller.selectors";
import {SellerNewDialogComponent} from "../seller-new-dialog/seller-new-dialog.component";
import {AuthenticationService} from "../../shared/services/login/authentication.service";
import {DELETE_ACCESS} from "../../shared/model/login/auth.constants";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";
import {DocumentGeneratorComponent} from "../../shared/components/document-generator/document-generator.component";
import {DealCopyDialogComponent} from "../deal-copy-dialog/deal-copy-dialog.component";

@Component({
    selector: "valumize-deal-details-page",
    templateUrl: "./deal-details-page.component.html",
    styleUrls: ["./deal-details-page.component.scss"],
    standalone: false,
    providers: [
        // Page-root comment-context prefix: one bulk-counts call per page covers every card under it.
        {
            provide: CARD_COMMENT_CONTEXT_PREFIX,
            useFactory: (store: Store) =>
                store.select(selectSelectedDealId).pipe(map((id) => id != null ? `/deals/deal_id=${id}` : null)),
            deps: [Store],
        },
    ],
})
export class DealDetailsPageComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthenticationService);

    dealId?: number;
    sellerId?: number;

    isLoading$ = this.store.select(isDealPageLoading);
    closingDate$ = this.store.select(selectSelectedDealClosingDate);
    selectedSeller$ = this.store.select(selectSelectedSeller);
    dealName$ = this.store.select(selectSelectedDealName);
    isEditDisabled$ = this.store.select(selectIsAnyDealFormInEditMode);
    errorMsg$ = this.store.select(selectSelectedDealErrorMsg);
    isArchived$ = this.store.select(selectSelectedDealIsArchived);

    dealNameIsEditable = false;

    subscriptions: Subscription[] = [];

    dealNameForm = this.formBuilder.group({
        dealName: this.formBuilder.control("", {nonNullable: true, validators: Validators.maxLength(100)}),
    });

    constructor() {
        this.subscriptions.push(this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.reloadPage();
            }
        }));
    }

    reloadPage(): void {
        this.dealId = parseInt(this.route.snapshot.paramMap.get("dealId") ?? "", 10);
        this.store.dispatch(DealActions.loadDealData({dealId: this.dealId}));
    }

    ngOnInit() {
        // One bulk-counts call per page (never per card — perf rule 1), reloaded when the entity id OR
        // the selected dataset changes so the has-comment icons match the current dataset.
        this.subscriptions.push(
            combineLatest([
                this.store.select(selectSelectedDealId).pipe(filter((id): id is number => id != null)),
                this.store.select(selectSelectedSourceDataset)
            ]).pipe(
                distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1])
            ).subscribe(([id]) =>
                this.store.dispatch(CommentCountsActions.load({inContext: `/deals/deal_id=${id}`}))
            )
        );

        this.subscriptions.push(
            this.store.select(selectSelectedDealName).pipe(map((dealName) => {
                    this.dealNameIsEditable = dealName.dealNameIsEditable;
                    this.dealNameForm.patchValue({dealName: dealName.name?.text});

                    if (dealName.dealNameIsEditable) {
                        this.dealNameForm.enable();
                    } else {
                        this.dealNameForm.disable();
                    }
                })
            ).subscribe());
    }

    scrollToComponent = (componentId: string) => document.getElementById(componentId)?.scrollIntoView({behavior: "auto"});

    editMode = () => this.store.dispatch(DealNameActions.edit());

    save() {
        if (this.dealNameForm.valid) {
            this.store.dispatch(DealNameActions.save(this.dealNameForm.getRawValue()));
        }
    }

    cancel = () => this.store.dispatch(DealNameActions.cancel());

    openNewSellerDialog() {
        if (this.dialog.openDialogs.length === 0) {
            const dialogRef = this.dialog.open(SellerNewDialogComponent);
            dialogRef.componentInstance.dealId = this.dealId;
        }
    }

    get showDeleteButton(): boolean {
        return this.authService.currentUser?.hasAnyPermission(DELETE_ACCESS) ?? false;
    }

    openDeleteConfirmation(): void {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = "Are you sure you want to delete this deal? This action cannot be undone.";

        dialogRef.afterClosed().pipe(
            map(result => {
                if (result && this.dealId != null) {
                    this.store.dispatch(DealActions.delete({ dealId: this.dealId }));
                }
            })
        ).subscribe();
    }

    openArchiveConfirmation(archived: boolean): void {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Are you sure you want to ${archived ? "archive" : "unarchive"} this deal?`;

        dialogRef.afterClosed().pipe(
            map(result => {
                if (result && this.dealId != null) {
                    this.store.dispatch(DealActions.archive({archived}));
                }
            })
        ).subscribe();
    }

    onDeleteButtonClick(event: Event): void {
        // Remove focus from the button to avoid the aria-hidden conflict. Without this, the focused button is hidden and triggers an accessibility error.
        (event.currentTarget as HTMLElement).blur();
    }

    openDocumentGeneratorDialog(){
        const dialogRef = this.dialog.open(DocumentGeneratorComponent);
        dialogRef.componentInstance.context = "DEAL";
    }

    openCopyDialog(): void {
        if (this.dialog.openDialogs.length === 0) {
            // The dialog self-initializes (dealId + form prefill) from the selected deal in the store.
            this.dialog.open(DealCopyDialogComponent);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
